/**
 * Enquiry Operations API
 * API functions for managing enquiry actions (take, update status, bulk operations)
 */

import { Enquiry, EnquiryStatus } from "../../types/enquiries";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";
import { getBuildVersion } from "../getDefaultHeaders";

type BookingStatus =
  | "not_started"
  | "new"
  | "in_progress"
  | "complete"
  | "rejected"
  | "cancel_request"
  | "lost"
  | "deleted";

interface BookingApiResponse {
  payload: {
    booking: {
      _id: string;
      booking_id?: string;
      status: BookingStatus;
      assigned_to?: string;
      assigned_to_name?: string;
      labels?: string[];
      customer_labels?: string[];
    };
  };
}

const authenticatedFetch = async (
  path: string,
  method: string,
  body: unknown,
  errorLabel: string,
  failureMessage: string
): Promise<Response> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const url = `${environmentVars?.CORE_API_BASE_URL}${path}`;

  let response;

  try {
    response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Build-Version": getBuildVersion()
      },
      body: JSON.stringify(body)
    });
  } catch (error) {
    errorHandler(error, errorLabel);
    throw error;
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, errorLabel);
    throw new Error(failureMessage);
  }

  return response;
};

const postBookingUpdate = async (
  enquiryId: string,
  body: Record<string, unknown>,
  errorLabel: string,
  failureMessage: string
): Promise<BookingApiResponse["payload"]["booking"]> => {
  const response = await authenticatedFetch(
    `/bookings/${enquiryId}`,
    "POST",
    body,
    errorLabel,
    failureMessage
  );

  const data: BookingApiResponse = await response.json();
  return data.payload.booking;
};

const mapBookingStatusToEnquiryStatus = (
  status: BookingStatus | undefined
): EnquiryStatus => {
  if (status === "new" || status === "not_started") {
    return "new";
  }
  if (status === "in_progress") {
    return "in_progress";
  }
  if (status === "complete") {
    return "complete";
  }
  if (status === "cancel_request" || status === "rejected") {
    return "cancelled";
  }
  if (status === "lost") {
    return "lost";
  }
  if (status === "deleted") {
    return "deleted";
  }

  return "new";
};

const mapBookingToEnquiry = (
  booking: BookingApiResponse["payload"]["booking"]
): Enquiry => ({
  _id: booking._id,
  reference: booking.booking_id ?? booking._id,
  status: mapBookingStatusToEnquiryStatus(booking.status),
  assigned_to: booking.assigned_to,
  assigned_to_name: booking.assigned_to_name,
  labels: booking.labels
});

/**
 * Shared helper for updating an enquiry's assignment.
 * Both takeEnquiryRequest and assignEnquiryRequest delegate to this.
 */
const updateEnquiryAssignment = async (
  enquiryId: string,
  userId: string | undefined,
  errorLabel: string,
  failureMessage: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { assigned_to: userId ?? "" },
    errorLabel,
    failureMessage
  );

  return mapBookingToEnquiry(updatedBooking);
};

/**
 * Takes an enquiry and assigns it to a user
 * This updates the booking by setting assigned_to to the current user's ID
 */
export const takeEnquiryRequest = async (
  enquiryId: string,
  userId: string
): Promise<Enquiry> =>
  updateEnquiryAssignment(
    enquiryId,
    userId,
    "Error taking enquiry",
    "Failed to take enquiry"
  );

/**
 * Maps an EnquiryStatus to the corresponding BookingStatus used by the Core API.
 * Note: 'cancelled' in the client maps to 'rejected' in the API.
 */
const mapEnquiryStatusToBookingStatus = (
  status: EnquiryStatus
): BookingStatus => {
  if (status === "cancelled") {
    return "rejected";
  }
  if (
    status === "new" ||
    status === "in_progress" ||
    status === "complete" ||
    status === "lost" ||
    status === "deleted"
  ) {
    return status;
  }

  const _exhaustive: never = status;
  throw new Error(`Unexpected enquiry status: ${_exhaustive}`);
};

/**
 * Updates the status of an enquiry via the Core API.
 * Routes to the appropriate API call based on the target status:
 * - 'deleted' uses a DELETE request (inspired by collins-ui bookingDetails.js sendDeleteRequest)
 * - 'complete' and 'cancelled' use dedicated request functions
 * - Other statuses (new, in_progress, lost) POST the status directly
 */
export const updateEnquiryStatusRequest = async (
  enquiryId: string,
  status: EnquiryStatus
): Promise<Enquiry> => {
  if (status === "deleted") {
    return deleteEnquiryRequest(enquiryId);
  }
  if (status === "complete") {
    return confirmEnquiryRequest(enquiryId);
  }
  if (status === "cancelled") {
    return rejectEnquiryRequest(enquiryId);
  }

  return setEnquiryStatusRequest(enquiryId, status);
};

/**
 * Sets an enquiry status directly via the Core API.
 * Used for statuses that simply update the booking status field
 * (new, in_progress, lost). Follows the same pattern as
 * collins-ui bookingDetails.js reopenBooking / completeBooking.
 */
const setEnquiryStatusRequest = async (
  enquiryId: string,
  status: EnquiryStatus
): Promise<Enquiry> => {
  const bookingStatus = mapEnquiryStatusToBookingStatus(status);
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: bookingStatus },
    `Error updating enquiry status to ${status}`,
    `Failed to update enquiry status to ${status}`
  );

  return mapBookingToEnquiry(updatedBooking);
};

/**
 * Deletes an enquiry via the Core API using a DELETE request.
 * Inspired by collins-ui bookingDetails.js sendDeleteRequest / BookingsProvider.delete.
 * The Core API soft-deletes the booking, so it can be restored later.
 */
export const deleteEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${enquiryId}`;

  let reference = enquiryId;

  try {
    const referenceResponse = await fetch(bookingUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (referenceResponse.ok) {
      const referenceData: BookingApiResponse = await referenceResponse.json();
      const existingBooking = referenceData.payload.booking;
      reference = existingBooking.booking_id ?? existingBooking._id;
    }
  } catch {
    // Ignore reference fetch errors
  }

  await authenticatedFetch(
    `/bookings/${enquiryId}`,
    "DELETE",
    { reason: "other" },
    "Error deleting enquiry",
    "Failed to delete enquiry"
  );

  return {
    _id: enquiryId,
    reference,
    status: "deleted"
  };
};

/**
 * Rejects an enquiry by setting its status to rejected
 * This updates the booking status via the Core API
 * Inspired by collins-ui bookingDetails.js rejectBooking function
 */
export const rejectEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: "rejected" },
    "Error rejecting enquiry",
    "Failed to reject enquiry"
  );

  return mapBookingToEnquiry(updatedBooking);
};

/**
 * Confirms an enquiry by setting its status to complete
 * This updates the booking status via the Core API
 */
export const confirmEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: "complete" },
    "Error confirming enquiry",
    "Failed to confirm enquiry"
  );

  return mapBookingToEnquiry(updatedBooking);
};

/**
 * Assigns or unassigns an enquiry via the Core API.
 * Posts { assigned_to: userId } to assign, or { assigned_to: "" } to unassign.
 */
export const assignEnquiryRequest = async (
  enquiryId: string,
  userId: string | undefined
): Promise<Enquiry> =>
  updateEnquiryAssignment(
    enquiryId,
    userId,
    "Error assigning enquiry",
    "Failed to assign enquiry"
  );

/**
 * Bulk assign enquiries to a user via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { assigned_to: userId },
 * matching collins-ui's BookingsManager.bulkUpdateBookings (bookingsManager.js:787-811)
 * and bookings.js:590-592 bulkAssignTo pattern.
 *
 * Pass an empty string to unassign all selected enquiries.
 */
export const bulkAssignTo = async (
  enquiryIds: string[],
  userId: string
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { assigned_to: userId }
    },
    "Error bulk assigning enquiries",
    "Failed to bulk assign enquiries"
  );

  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk set status for enquiries via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { status },
 * matching collins-ui's BookingsManager.bulkUpdateBookings (bookingsManager.js:787-811)
 * and bookings.js:595-597 bulkSetStatus pattern.
 */
export const bulkSetStatus = async (
  enquiryIds: string[],
  status: EnquiryStatus
): Promise<{ success: boolean; count: number }> => {
  const bookingStatus = mapEnquiryStatusToBookingStatus(status);
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { status: bookingStatus }
    },
    "Error bulk setting status",
    "Failed to bulk set status"
  );

  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk add labels to enquiries via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { labels: labelId },
 * matching collins-ui's bookings.js:600-602 bulkAddLabel pattern.
 */
export const bulkAddLabels = async (
  enquiryIds: string[],
  labelId: string
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { labels: labelId }
    },
    "Error bulk adding label",
    "Failed to bulk add label"
  );

  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk remove labels from enquiries via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { '-labels': labelId },
 * matching collins-ui's bookings.js:605-607 bulkRemoveLabel pattern.
 */
export const bulkRemoveLabels = async (
  enquiryIds: string[],
  labelId: string
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { "-labels": labelId }
    },
    "Error bulk removing label",
    "Failed to bulk remove label"
  );

  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk set follow-up date for enquiries via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { follow_up: date },
 * matching collins-ui's bookings.js:610-612 bulkFollowUpDate pattern.
 */
export const bulkSetFollowUp = async (
  enquiryIds: string[],
  followUpDate: string
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { follow_up: followUpDate }
    },
    "Error bulk setting follow-up date",
    "Failed to bulk set follow-up date"
  );

  return { success: true, count: enquiryIds.length };
};

/**
 * Acknowledges (clears) notifications on a single booking via the Core API.
 * Marks all notifications of the given types as read.
 * Reference: collins-ui BookingsManager.acknowledgeNotifications (bookingsManager.js:932-974)
 */
export const acknowledgeBookingNotifications = async (
  bookingId: string,
  types: string[]
): Promise<void> => {
  await authenticatedFetch(
    `/bookings/${bookingId}/notifications`,
    "POST",
    { action: "mark-read", types },
    "Error clearing booking notifications",
    "Failed to clear booking notifications"
  );
};

/**
 * Bulk reject enquiries via the Core API.
 * Uses POST /bookings with action: 'bulkUpdate' and values: { status: 'rejected' },
 * matching collins-ui's BookingsManager.bulkUpdateBookings (bookingsManager.js:787-811)
 * and bookings.js:555-566 bulkRejectBookings pattern.
 */
export const bulkReject = async (
  enquiryIds: string[]
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings",
    "POST",
    {
      action: "bulkUpdate",
      bookingIds: enquiryIds,
      values: { status: "rejected" }
    },
    "Error bulk rejecting enquiries",
    "Failed to bulk reject enquiries"
  );

  return { success: true, count: enquiryIds.length };
};

export type BulkDeleteReason = "spam" | "duplicate" | "other";

/**
 * Bulk delete enquiries via the Core API.
 * Calls DELETE /bookings/bulk-delete with bookingIds and an optional reason.
 * Defaults to "other" when no reason is provided.
 */
export const bulkDelete = async (
  enquiryIds: string[],
  reason: BulkDeleteReason = "other"
): Promise<{ success: boolean; count: number }> => {
  await authenticatedFetch(
    "/bookings/bulk-delete",
    "DELETE",
    { bookingIds: enquiryIds, reason },
    "Error bulk deleting enquiries",
    "Failed to bulk delete enquiries"
  );

  return { success: true, count: enquiryIds.length };
};
